import type * as THREE from 'three/webgpu';

/** A layer no camera looks at (as in ShaderWarmup). */
const WAITING_LAYER = 31;

/**
 * Starts the shaders of objects that aren't on screen yet (another hollow's, say) without
 * showing them. Each material's objects are drawn for a single frame, one material per frame:
 * with AsyncPipelines that starts the compile in the background and draws nothing, since the
 * pipeline isn't ready yet. Building each shader's code is CPU work, so doing one per frame
 * keeps frames short. Then the objects go back on the waiting layer.
 *
 * The roots must be visible while this runs. When `done`, call `restore()`.
 */
export class BackgroundWarmup {
  private readonly groups: THREE.Object3D[][];
  private readonly masks = new Map<THREE.Object3D, number>();
  private next = 0;

  constructor(roots: THREE.Object3D[]) {
    const byMaterial = new Map<string, THREE.Object3D[]>();
    for (const root of roots) {
      root.traverse((o) => {
        const m = (o as THREE.Mesh).material;
        if (!m) return;
        const key = Array.isArray(m) ? m.map((x) => x.uuid).join() : m.uuid;
        let group = byMaterial.get(key);
        if (!group) {
          group = [];
          byMaterial.set(key, group);
        }
        group.push(o);
        this.masks.set(o, o.layers.mask);
        o.layers.set(WAITING_LAYER);
      });
    }
    this.groups = [...byMaterial.values()];
  }

  get done(): boolean {
    return this.next > this.groups.length;
  }

  /** Put last frame's material back to wait, and draw the next one this frame. */
  step(): void {
    if (this.done) return;
    if (this.next > 0) for (const o of this.groups[this.next - 1]) o.layers.set(WAITING_LAYER);
    if (this.next < this.groups.length) {
      for (const o of this.groups[this.next]) o.layers.mask = this.masks.get(o) ?? 1;
    }
    this.next++;
  }

  /** Give every object its own layers back. */
  restore(): void {
    for (const [o, mask] of this.masks) o.layers.mask = mask;
  }
}
